import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Bell, BellOff, Loader2, Smartphone, TestTube2 } from "lucide-react";

/**
 * Push notification toggle for admin devices.
 *
 * Reads VAPID public key from /admin/push/config, subscribes via the browser's
 * PushManager and registers the subscription with our backend. Skips the UI
 * entirely on unsupported browsers (notably iOS Safari outside of installed PWAs)
 * and shows a calm "not supported" notice instead.
 */

// ---- Helpers ----
const supportsPush = () => "Notification" in window && "serviceWorker" in navigator && "PushManager" in window;
const urlBase64ToUint8Array = (b64) => {
  const padding = "=".repeat((4 - (b64.length % 4)) % 4);
  const base64 = (b64 + padding).replace(/-/g, "+").replace(/_/g, "/");
  const raw = atob(base64);
  const arr = new Uint8Array(raw.length);
  for (let i = 0; i < raw.length; i++) arr[i] = raw.charCodeAt(i);
  return arr;
};

export const PushNotificationsToggle = () => {
  const [supported] = useState(supportsPush());
  const [permission, setPermission] = useState(supportsPush() ? Notification.permission : "denied");
  const [config, setConfig] = useState(null); // {public_key, enabled}
  const [subscribed, setSubscribed] = useState(false);
  const [busy, setBusy] = useState(false);
  const [testing, setTesting] = useState(false);

  useEffect(() => {
    if (!supported) return;
    let dead = false;
    (async () => {
      try {
        const { data } = await api.get("/admin/push/config");
        if (!dead) setConfig(data);
      } catch (e) {
        if (!dead) setConfig({ public_key: "", enabled: false });
      }
      try {
        const reg = await navigator.serviceWorker.ready;
        const sub = await reg.pushManager.getSubscription();
        if (!dead) setSubscribed(!!sub);
      } catch (e) {
        if (!dead) setSubscribed(false);
      }
    })();
    return () => { dead = true; };
  }, [supported]);

  const subscribe = async () => {
    if (!config?.public_key) return toast.error("Push not configured on the server yet");
    setBusy(true);
    try {
      const perm = await Notification.requestPermission();
      setPermission(perm);
      if (perm !== "granted") {
        toast.error("Notifications blocked — enable in your browser settings.");
        return;
      }
      const reg = await navigator.serviceWorker.ready;
      const sub = await reg.pushManager.subscribe({
        userVisibleOnly: true,
        applicationServerKey: urlBase64ToUint8Array(config.public_key),
      });
      const subJson = sub.toJSON();
      await api.post("/admin/push/subscribe", {
        endpoint: subJson.endpoint,
        keys: subJson.keys,
        label: navigator.platform || "Device",
        user_agent: navigator.userAgent.slice(0, 200),
      });
      setSubscribed(true);
      toast.success("Push notifications enabled on this device");
    } catch (e) {
      console.warn(e);
      toast.error(e.response?.data?.detail || "Couldn't enable notifications");
    } finally {
      setBusy(false);
    }
  };

  const unsubscribe = async () => {
    setBusy(true);
    try {
      const reg = await navigator.serviceWorker.ready;
      const sub = await reg.pushManager.getSubscription();
      if (sub) {
        const endpoint = sub.endpoint;
        await sub.unsubscribe();
        try {
          await api.post("/admin/push/unsubscribe", { endpoint, keys: { p256dh: "", auth: "" } });
        } catch (e) { /* best-effort */ }
      }
      setSubscribed(false);
      toast.success("Push notifications disabled on this device");
    } catch (e) {
      toast.error("Couldn't disable notifications");
    } finally {
      setBusy(false);
    }
  };

  const sendTest = async () => {
    setTesting(true);
    try {
      const { data } = await api.post("/admin/push/test");
      toast.success(`Test sent to ${data.delivered} device${data.delivered === 1 ? "" : "s"} — give it a few seconds.`);
    } catch (e) {
      toast.error("Test failed — check that this device is subscribed.");
    } finally {
      setTesting(false);
    }
  };

  if (!supported) {
    return (
      <div className="sm:col-span-2 rounded-md bg-white/[0.03] border border-white/10 p-4 text-sm text-slate-400 flex items-start gap-3" data-testid="push-not-supported">
        <BellOff className="size-4 mt-0.5 shrink-0" />
        <div>
          <strong className="text-white block">Push notifications unavailable on this browser.</strong>
          On Android, install Spark from Chrome (long-press home screen → app menu → <em>Install app</em>) for push support. iOS only allows push from installed PWAs running iOS 16.4+.
        </div>
      </div>
    );
  }

  return (
    <div className="sm:col-span-2 rounded-md bg-white/[0.03] border border-white/10 p-4 sm:p-5" data-testid="push-toggle">
      <div className="flex items-start gap-3 flex-wrap">
        <div className={`size-9 rounded-md flex items-center justify-center shrink-0 ${subscribed ? "bg-spark-yellow/15 text-spark-yellow" : "bg-white/5 text-slate-400"}`}>
          {subscribed ? <Bell className="size-4" /> : <BellOff className="size-4" />}
        </div>
        <div className="flex-1 min-w-0">
          <p className="font-display uppercase tracking-wider text-xs font-bold text-white">
            {subscribed ? "Push notifications ON for this device" : "Push notifications OFF on this device"}
          </p>
          <p className="text-xs text-slate-400 mt-1 leading-relaxed">
            Get pinged on this phone/tablet when a customer books, uploads payment proof, places a shop order, approves a quote, or leaves a review.
            {permission === "denied" && (
              <span className="block mt-1 text-red-300"><strong>Browser blocked notifications</strong> — clear the block in your browser site settings to re-enable.</span>
            )}
          </p>
          <div className="flex flex-wrap items-center gap-2 mt-3">
            {!subscribed ? (
              <button onClick={subscribe} disabled={busy || !config?.enabled} className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-md bg-spark-yellow text-spark-ink text-xs uppercase tracking-wider font-bold hover:bg-yellow-400 disabled:opacity-50" data-testid="push-enable-btn">
                {busy ? <Loader2 className="size-3.5 animate-spin" /> : <Bell className="size-3.5" />}
                {busy ? "Enabling…" : "Enable on this device"}
              </button>
            ) : (
              <>
                <button onClick={sendTest} disabled={testing} className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-md bg-spark-teal text-white text-xs uppercase tracking-wider font-bold hover:bg-spark-teal-deep disabled:opacity-50" data-testid="push-test-btn">
                  {testing ? <Loader2 className="size-3.5 animate-spin" /> : <TestTube2 className="size-3.5" />} Send test
                </button>
                <button onClick={unsubscribe} disabled={busy} className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-md bg-white/5 hover:bg-white/10 text-slate-300 text-xs uppercase tracking-wider font-semibold disabled:opacity-50" data-testid="push-disable-btn">
                  {busy ? <Loader2 className="size-3.5 animate-spin" /> : <BellOff className="size-3.5" />} Turn off
                </button>
              </>
            )}
            <span className="inline-flex items-center gap-1 text-[10px] uppercase tracking-wider font-bold text-slate-500"><Smartphone className="size-3" /> Per-device setting</span>
          </div>
        </div>
      </div>
    </div>
  );
};

export default PushNotificationsToggle;
